/**
 * Search Suggestions Dropdown
 *
 * Unified dropdown rendered below the search bar. Displays two grouped
 * sections inside a single ARIA `listbox`:
 *
 *   1. Recent — entries from `useSearchHistory` (filtered by prefix when the
 *      user has typed ≥ minLength characters).
 *   2. Suggestions — server-side title completions from the `/suggest` endpoint.
 *
 * The component is purely presentational — keyboard handling, fetching, and
 * dropdown open/close state live in the parent `SearchBar`.
 *
 * Accessibility: implements the W3C ARIA 1.2 combobox + listbox pattern.
 * Section headers use `role="presentation"` so screen readers don't announce
 * them as options. The active option is tracked via `aria-activedescendant`
 * on the input (DOM focus stays on the input).
 */

import { useTranslation } from 'react-i18next';
import { Fragment } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import type { DropdownRow } from '../../searchCore/rows';

export type { DropdownRow };

interface SearchSuggestionsDropdownProps {
  /** Combined, ordered list of rows shown in the listbox. */
  rows: DropdownRow[];
  /** Index in `rows` of the keyboard-active option (-1 = none). */
  activeIndex: number;
  /** Current query string — used to highlight matched substring in suggestions. */
  query: string;
  /** Whether suggestions are currently being fetched. */
  isLoading: boolean;
  /** Called when a row is clicked or activated. */
  onSelect: (row: DropdownRow) => void;
  /** Called when the X button on a history row is clicked. */
  onRemoveHistory: (value: string) => void;
  /** Called when "Clear search history" is clicked. */
  onClearHistory: () => void;
  /** ID for the listbox element — must match `aria-controls` on the input. */
  listboxId: string;
  /** Prefix used to build `aria-activedescendant` ids. */
  optionIdPrefix: string;
  /** Whether the history section has any items (for separator rendering). */
  hasAnyHistory: boolean;
  /**
   * Resolve a brand SVG path for a suggestion's `source`. Provided by the
   * parent so the dropdown stays decoupled from asset imports.
   */
  resolveSourceIcon: (source: string | undefined) => string;
  /**
   * Convert a `source_type` raw value to its facet display name (e.g.
   * `"doc_page"` → `"Documentation Page"`). Falls back to the raw value if
   * facets haven't loaded yet.
   */
  resolveSourceTypeLabel: (sourceType: string | undefined) => string | undefined;
}

function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const lower = text.toLowerCase();
  const needle = trimmed.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box
        component="mark"
        sx={{
          bgcolor: 'transparent',
          color: 'inherit',
          fontWeight: 600,
        }}
      >
        {text.slice(idx, idx + needle.length)}
      </Box>
      {text.slice(idx + needle.length)}
    </>
  );
}

/**
 * Compute section boundaries from the rows array so we can render the
 * "RECENT" / "SUGGESTIONS" headers at the right positions.
 */
function findSectionBoundaries(rows: DropdownRow[]): { firstSuggestionIdx: number } {
  const idx = rows.findIndex((r) => r.kind === 'suggestion');
  return { firstSuggestionIdx: idx };
}

export function SearchSuggestionsDropdown({
  rows,
  activeIndex,
  query,
  isLoading,
  onSelect,
  onRemoveHistory,
  onClearHistory,
  listboxId,
  optionIdPrefix,
  hasAnyHistory,
  resolveSourceIcon,
  resolveSourceTypeLabel,
}: SearchSuggestionsDropdownProps) {
  const { t } = useTranslation('search');
  const { firstSuggestionIdx } = findSectionBoundaries(rows);

  if (rows.length === 0 && !isLoading) return null;

  return (
    <>
      {/* Loading indicator (only visible while a request is in flight) */}
      <Box sx={{ height: 2 }}>
        {isLoading && (
          <LinearProgress
            sx={{ height: 2 }}
            aria-label={t('suggestions.loading')}
          />
        )}
      </Box>

      <Divider />

      <Box
        component="ul"
        role="listbox"
        id={listboxId}
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          maxHeight: { xs: '60vh', sm: 480 },
          overflowY: 'auto',
        }}
      >
        {rows.map((row, index) => {
          const isFirstHistory = index === 0 && row.kind === 'history';
          const isFirstSuggestion = index === firstSuggestionIdx && row.kind === 'suggestion';

          return (
            <Fragment key={`${row.kind}-${row.kind === 'history' ? row.value : row.value.text}-${index}`}>
              {isFirstHistory && (
                <SectionHeader label={t('suggestions.recentHeader')} />
              )}
              {isFirstSuggestion && (
                <>
                  {hasAnyHistory && firstSuggestionIdx > 0 && (
                    <Box component="li" role="presentation" sx={{ py: 0.5 }}>
                      <Divider />
                    </Box>
                  )}
                  <SectionHeader label={t('suggestions.suggestionsHeader')} />
                </>
              )}
              <SuggestionRow
                row={row}
                index={index}
                active={index === activeIndex}
                query={query}
                optionIdPrefix={optionIdPrefix}
                onSelect={onSelect}
                onRemoveHistory={onRemoveHistory}
                resolveSourceIcon={resolveSourceIcon}
                resolveSourceTypeLabel={resolveSourceTypeLabel}
              />
            </Fragment>
          );
        })}
      </Box>

      {hasAnyHistory && (
        <>
          <Divider />
          <Box
            component="button"
            type="button"
            onClick={onClearHistory}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              width: '100%',
              py: 1,
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            <DeleteSweepIcon fontSize="small" />
            <Typography variant="body2">{t('suggestions.clearHistory')}</Typography>
          </Box>
        </>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function SectionHeader({ label }: { label: string }) {
  return (
    <Box
      component="li"
      role="presentation"
      sx={{
        px: 2,
        pt: 1,
        pb: 0.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

interface SuggestionRowProps {
  row: DropdownRow;
  index: number;
  active: boolean;
  query: string;
  optionIdPrefix: string;
  onSelect: (row: DropdownRow) => void;
  onRemoveHistory: (value: string) => void;
  resolveSourceIcon: (source: string | undefined) => string;
  resolveSourceTypeLabel: (sourceType: string | undefined) => string | undefined;
}

function SuggestionRow({
  row,
  index,
  active,
  query,
  optionIdPrefix,
  onSelect,
  onRemoveHistory,
  resolveSourceIcon,
  resolveSourceTypeLabel,
}: SuggestionRowProps) {
  const text = row.kind === 'history' ? row.value : row.value.text;
  const sourceTypeLabel =
    row.kind === 'suggestion' ? resolveSourceTypeLabel(row.value.source_type) : undefined;
  const sourceIconSrc =
    row.kind === 'suggestion' ? resolveSourceIcon(row.value.source) : undefined;

  return (
    <Box
      component="li"
      id={`${optionIdPrefix}-${index}`}
      role="option"
      aria-selected={active}
      // Use mousedown rather than click so we beat the input's blur/close handler.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(row);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        cursor: 'pointer',
        bgcolor: active ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {row.kind === 'history' ? (
        <HistoryIcon
          fontSize="small"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        />
      ) : sourceIconSrc ? (
        <Box
          component="img"
          src={sourceIconSrc}
          alt=""
          aria-hidden
          sx={{
            width: 18,
            height: 18,
            flexShrink: 0,
            objectFit: 'contain',
          }}
        />
      ) : (
        <SearchIcon
          fontSize="small"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.95rem',
        }}
      >
        {row.kind === 'suggestion' ? highlightMatch(text, query) : text}
      </Box>
      {sourceTypeLabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            bgcolor: 'action.hover',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            flexShrink: 0,
            fontSize: '0.7rem',
          }}
        >
          {sourceTypeLabel}
        </Typography>
      )}
      {row.kind === 'history' && (
        <IconButton
          size="small"
          edge="end"
          // Stop the row's mousedown handler from firing.
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemoveHistory(row.value);
          }}
          aria-label={`Remove "${row.value}"`}
          sx={{
            opacity: 0.6,
            '&:hover': { opacity: 1 },
            ml: 0.5,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
